import React, { useMemo, useEffect } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  MarkerType,
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  BackgroundVariant,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { TaskPlan, PlanRun, TaskStatus, TaskType } from "../types";
import FlowTaskNode from "./FlowTaskNode";

interface PlanFlowRunnerProps {
  plan: TaskPlan;
  run: PlanRun;
}

const nodeWidth = 260;
const nodeHeight = 120;

const RunTaskNode = ({ data }: any) => {
  const { label, type, status, durationMinutes } = data;

  // Status color logic
  const getStatusStyles = (s: TaskStatus) => {
    switch (s) {
      case TaskStatus.COMPLETED:
        return "border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20";
      case TaskStatus.RUNNING:
      case TaskStatus.WAITING_FOR_USER:
        return "border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 animate-pulse ring-2 ring-blue-500/20";
      case TaskStatus.FAILED:
        return "border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20";
      case TaskStatus.SKIPPED:
        return "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 opacity-75";
      default: // PENDING
        return "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800";
    }
  };

  const styles = getStatusStyles(status || TaskStatus.PENDING);
  const textColor =
    status === TaskStatus.SKIPPED
      ? "text-slate-400"
      : "text-slate-800 dark:text-slate-100";

  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-sm border transition-all duration-300 w-[240px] break-words ${styles}`}
    >
      <Handle type="target" position={Position.Left} className="opacity-0" />
      <div className="flex flex-col">
        <span className={`font-bold text-sm leading-tight ${textColor}`}>
          {label}
        </span>
        <div className="flex items-center gap-2 mt-2 text-[10px] opacity-80">
          {type === TaskType.AUTOMATED ? (
            <span className="uppercase text-purple-600 dark:text-purple-400 font-bold">
              AUTO
            </span>
          ) : null}
          <span className="text-slate-500 dark:text-slate-400">
            {durationMinutes}m
          </span>
        </div>
      </div>
      {status === TaskStatus.RUNNING && (
        <div className="absolute top-2 right-2 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </div>
      )}
      <Handle type="source" position={Position.Right} className="opacity-0" />
    </div>
  );
};

const nodeTypes = {
  runTask: RunTaskNode,
};

const getLayoutedElements = (plan: TaskPlan, run: PlanRun) => {
  const tasks = plan.tasks || [];
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setGraph({ rankdir: "LR" });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Create Nodes
  const nodes: Node[] = tasks
    .filter((t) => t.type !== TaskType.SECTION)
    .map((t) => {
      const state = run.taskStates?.[t.id] || { status: TaskStatus.PENDING };
      return {
        id: t.id,
        type: "runTask",
        data: {
          label: t.name,
          type: t.type,
          durationMinutes: t.durationMinutes,
          status: state.status,
        },
        position: { x: 0, y: 0 },
      };
    });

  // Create Edges
  const edges: Edge[] = [];
  tasks.forEach((t) => {
    if (t.type === TaskType.SECTION) return;
    t.dependencyIds.forEach((depId) => {
      const depTask = tasks.find((dt) => dt.id === depId);
      if (!depTask || depTask.type === TaskType.SECTION) return;

      edges.push({
        id: `${depId}-${t.id}`,
        source: depId,
        target: t.id,
        type: "default",
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
        style: { stroke: "#94a3b8", strokeWidth: 2 }, // Slate-400
        animated: true,
      });
    });
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  });

  return { nodes, edges };
};

const PlanFlowRunner: React.FC<PlanFlowRunnerProps> = ({ plan, run }) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => getLayoutedElements(plan, run),
    [plan.id, run.id],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // React to status changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = getLayoutedElements(plan, run);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [plan, run, setNodes, setEdges]);

  return (
    <div className="h-full w-full bg-slate-50 dark:bg-slate-900 border-none outline-none">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-right"
        nodesConnectable={false}
        nodesDraggable={true}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          className="dark:bg-slate-950 bg-slate-50"
          color="#cbd5e1"
        />
        <Controls className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm [&>button]:bg-white [&>button]:dark:bg-slate-800 [&>button]:border-slate-200 [&>button]:dark:border-slate-700 [&_svg]:fill-slate-500 [&_svg]:dark:fill-slate-400" />
        <MiniMap
          nodeColor={() => "#6366f1"}
          maskColor="rgba(0, 0, 0, 0.1)"
          className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm"
        />
      </ReactFlow>
    </div>
  );
};

export default PlanFlowRunner;
